import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { TailSpin as Loading } from 'react-loading-icons';
import './seating.scss';
import loaderStyle from '../../../loader/loader.scss';
import { WIX_INSTANCE_PARAMS } from '../../../actions/component_actions';

const SeatingRenderer = ({ eventId, cart, onSeatsSelected, onHoldTokenGenerated }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const chartRef = useRef(null);
  const seatingChartInstance = useRef(null);

  // Use refs for callbacks to avoid recreating chart on callback changes
  const onSeatsSelectedRef = useRef(onSeatsSelected);
  const onHoldTokenGeneratedRef = useRef(onHoldTokenGenerated);

  useEffect(() => {
    onSeatsSelectedRef.current = onSeatsSelected;
    onHoldTokenGeneratedRef.current = onHoldTokenGenerated;
  }, [onSeatsSelected, onHoldTokenGenerated]);

  // Calculate total quantity from cart (0 means unlimited for seating-first flow)
  const getTotalCartQuantity = () => {
    if (!cart || !Array.isArray(cart) || cart.length === 0) return 0;
    return cart.reduce((total, item) => total + (item.quantity || 0), 0);
  };

  // Find ticket for a given category
  const findTicketForCategory = (categoryKey) => {
    if (!config || !config.ticketAssignments) return null;

    // Find the ticket assignment for this category
    const assignment = config.ticketAssignments.find((a) => String(a.category) === String(categoryKey));

    if (!assignment) return null;

    // Find the full ticket data from config.tickets
    const ticket = config.tickets?.find((t) => String(t.id) === String(assignment.ticketId) || t.ticket_uuid === assignment.ticketUuid);

    return ticket || { id: assignment.ticketId, name: assignment.categoryLabel };
  };

  useEffect(() => {
    // Fetch seating chart configuration
    const fetchConfig = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.EVENTVIEWER_SERVER}/api/v1/events/${eventId}/seating${WIX_INSTANCE_PARAMS}`);

        if (response.data.success) {
          setConfig(response.data.config);
        } else {
          setError('Failed to load seating chart');
        }
      } catch (err) {
        console.error('Error fetching seating config:', err);
        if (err.response?.status === 404) {
          setError('No seating chart available for this event');
        } else {
          setError('Failed to load seating chart. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      fetchConfig();
    }
  }, [eventId]);

  useEffect(() => {
    // Load seats.io chart renderer when config is ready
    if (!config || !window.seatsio || seatingChartInstance.current) return;

    const maxSeats = getTotalCartQuantity();

    try {
      // Format currency
      const formatPrice = (price) => {
        const currency = config.currency || 'USD';
        const formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
        });
        return formatter.format(price);
      };

      // Build seating chart configuration
      const chartConfig = {
        divId: 'seating-chart-container',
        workspaceKey: config.publicKey, // Workspace-specific public key from API
        event: config.eventKey,
        region: config.region || 'na', // Use workspace region
        session: 'start',
        pricing:
          config.pricing && config.pricing.length > 0
            ? {
                prices: config.pricing,
                priceFormatter: formatPrice,
              }
            : undefined,
        colorScheme: 'light',
        showMinimap: true,
        language: 'en',
      };

      // Only set maxSelectedObjects if we have a cart (ticket-first flow)
      // Otherwise allow unlimited selection (seating-first flow)
      if (maxSeats > 0) {
        chartConfig.maxSelectedObjects = maxSeats;
      }

      // Add callbacks using refs to avoid stale closures
      chartConfig.onObjectSelected = (object) => {
        const ticket = findTicketForCategory(object.category.key);

        if (onSeatsSelectedRef.current) {
          onSeatsSelectedRef.current({
            seatId: object.id,
            seatLabel: object.labels.displayedLabel || object.label,
            category: object.category.label,
            categoryKey: object.category.key,
            ticketId: ticket?.id || null,
            action: 'select',
          });
        }
      };

      chartConfig.onObjectDeselected = (object) => {
        if (onSeatsSelectedRef.current) {
          onSeatsSelectedRef.current({
            seatId: object.id,
            seatLabel: object.labels.own || object.label,
            category: object.category.label,
            categoryKey: object.category.key,
            action: 'deselect',
          });
        }
      };

      chartConfig.onSessionInitialized = (holdToken) => {
        // Pass hold token to parent component via callback
        // Parent will manage this in state
        if (onHoldTokenGeneratedRef.current) {
          onHoldTokenGeneratedRef.current(holdToken);
        }
      };

      chartConfig.onChartRendered = () => {
        setLoading(false);
      };

      chartConfig.onChartRenderingFailed = () => {
        setError('Failed to render seating chart. Please refresh the page.');
        setLoading(false);
      };

      // Initialize seats.io chart with workspace-specific public key
      seatingChartInstance.current = new window.seatsio.SeatingChart(chartConfig).render();
    } catch (err) {
      console.error('Error initializing seating chart:', err);
      setError('Failed to initialize seating chart');
      setLoading(false);
    }
  }, [config]); // Only re-initialize if config changes (not cart - we're in seating-first mode)

  if (loading) {
    return (
      <div className={loaderStyle.container} style={{ position: 'relative' }}>
        <Loading strokeWidth={3} height="2em" width={30} stroke="#38434B" className="loading-indicator" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="seating-chart-error">
        <p>{error}</p>
      </div>
    );
  }

  if (!config) {
    return null;
  }

  return (
    <div className="seating-chart-wrapper">
      <div className="seating-chart-header" />
      <div id="seating-chart-container" ref={chartRef} className="seating-chart-container" />
    </div>
  );
};

export default SeatingRenderer;
